// src/api/classificationApi.ts

import {
  AgentType,
  ClassificationResponse,
  ClassificationRequestParams,
} from "../interfaces/classification";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { parseErrorResponse } from "./utils/errorHandler";
import { normalizeAccessToken } from "../utils/typeHelpers";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { streamErrorToPostHog, ApiNames } from "./utils/captureError";

const log = createLogger("ClassificationApi");

// Base URL for the API - following the same pattern as other APIs
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for classification API errors
 * Extends the pattern from ApiRequestError
 */
export class ClassificationApiError extends Error {
  errorCode: string;
  statusCode: number;
  requestId?: string;
  details?: any;

  constructor(
    message: string,
    errorCode: string,
    statusCode: number,
    requestId?: string,
    details?: any
  ) {
    super(message);
    this.name = "ClassificationApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.requestId = requestId;
    this.details = details;
  }
}

/**
 * Create an XML file from OOXML content
 * Utility function to convert string content to File object
 */
export function createXmlFileForClassification(
  ooxmlContent: string,
  filename: string = "document.xml"
): File {
  const blob = new Blob([ooxmlContent], { type: "application/xml" });
  return new File([blob], filename, { type: "application/xml" });
}

/**
 * Classify a document to determine the appropriate agent
 *
 * This function uploads the document to the classification endpoint
 * and returns the recommended agent type based on content analysis
 *
 * @param file XML file containing document content (OOXML from Word)
 * @param params Optional request parameters for caching and tracking
 * @returns Promise with the classification response
 */
export async function classifyDocument(
  file: File,
  params?: ClassificationRequestParams,
  accessToken?: string
): Promise<ClassificationResponse> {
  log.info("Starting document classification", {
    fileName: file.name,
    fileSize: file.size,
    hasParams: !!params,
    params: params || {},
  });

  // Create form data with the file
  const formData = new FormData();
  formData.append("file", file);

  // Add optional form fields if provided
  if (params?.id) {
    formData.append("id", params.id);
  }

  if (params?.bypass_cache !== undefined) {
    formData.append("bypass_cache", params.bypass_cache.toString());
  }

  if (params?.details) {
    formData.append("details", JSON.stringify(params.details));
  }

  if (params?.doc_id) {
    formData.append("doc_id", params.doc_id);
  }

  const url = `${API_BASE_URL}/classification/general/`;

  try {
    log.info("Sending classification request", { url, accessToken });

    const request = constructRequest(url, "POST", normalizeAccessToken(accessToken), formData);

    const response = await fetch(request);

    // Handle non-OK responses through the shared parser: it also runs the 401
    // refresh bridge, so an expired token triggers a token refresh instead of
    // every subsequent request failing with the same stale token.
    if (!response.ok) {
      log.error("Classification API returned error response", {
        status: response.status,
        statusText: response.statusText,
      });

      const errorData = await parseErrorResponse(response);
      throw new ClassificationApiError(
        errorData.message,
        errorData.error_code,
        errorData.status_code,
        errorData.request_id,
        errorData.details
      );
    }

    // Parse successful response
    const responseData = (await response.json()) as ClassificationResponse;

    log.info("Document classification successful", {
      documentType: responseData.data.document_type.type,
      agentType: responseData.data.agent_recommendation.agent_type,
      confidence: responseData.data.agent_recommendation.confidence_score,
      cached: responseData.metadata.cached,
      processingTime: responseData.metadata.processing_time_ms,
    });

    return responseData;
  } catch (error) {
    // Re-throw ClassificationApiError as-is
    if (error instanceof ClassificationApiError) {
      streamErrorToPostHog(error, ApiNames.CLASSIFICATION_API);
      throw error;
    }

    // Handle client-side errors with helpful, contextual messages
    log.error("Client error during classification", {
      error: error instanceof Error ? error.message : String(error),
    });

    const clientError = createClientErrorInfo(error, "file_upload");
    const classificationError = new ClassificationApiError(
      clientError.message,
      clientError.errorCode,
      0
    );
    streamErrorToPostHog(classificationError, ApiNames.CLASSIFICATION_API);
    throw classificationError;
  }
}

/**
 * Map API agent types to internal agent IDs
 *
 * This function converts the classification API's agent types
 * to the internal agent IDs used by the UI components
 */
export function mapAgentTypeToInternalId(agentType: AgentType): string {
  const mapping: Record<AgentType, string> = {
    redline_analyzer: "red-line-analyzer",
    executed_contracts: "contract-qa",
    document_generator: "drafting-assistant",
    general_agent: "ccm-space",
    Unknown: "ccm-space", // Default fallback to CCM Space
  };

  const mappedId = mapping[agentType];

  log.debug("Mapped agent type to internal ID", {
    apiAgentType: agentType,
    internalId: mappedId,
  });

  return mappedId;
}
